import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  X, Tag, Plus, Check, Minus, RefreshCw, Loader2, 
  CheckCircle2, AlertCircle, Sparkles 
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Tag category colors
const categoryColors = {
  influencer: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  campaign: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  style: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  custom: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
};

/**
 * Batch Tag Modal - Tag multiple items at once
 */
const BatchTagModal = ({ 
  isOpen, 
  onClose, 
  selectedItems, // Array of {id, type}
  token,
  onSuccess 
}) => {
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]); // Tags to apply
  const [action, setAction] = useState('add'); // 'add', 'remove', 'set'
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [commonTags, setCommonTags] = useState([]);
  const [allItemTags, setAllItemTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [showCreateTag, setShowCreateTag] = useState(false);

  // Fetch user's tags and get tag summary for selected items
  useEffect(() => {
    if (!isOpen || !token) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all user tags
        const tagsResponse = await axios.get(`${API}/api/tags/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllTags(tagsResponse.data.tags || []);

        // Fetch tag summary for selected items
        if (selectedItems.length > 0) {
          const contentIds = selectedItems.map(item => item.id).join(',');
          const summaryResponse = await axios.get(
            `${API}/api/tags/content-summary?content_ids=${contentIds}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCommonTags(summaryResponse.data.common_tags || []);
          setAllItemTags(summaryResponse.data.all_tags || []);
        }
      } catch (error) {
        console.error('Failed to fetch tag data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, token, selectedItems]);

  // Toggle tag selection
  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const exists = prev.find(t => t.id === tag.id);
      if (exists) {
        return prev.filter(t => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  // Check if tag is selected
  const isTagSelected = (tagId) => {
    return selectedTags.some(t => t.id === tagId);
  };

  // Get tag status for the selected items
  const getTagStatus = (tagId) => {
    const itemTag = allItemTags.find(t => t.id === tagId);
    if (!itemTag) return 'none';
    if (itemTag.is_common) return 'all'; // Present in all items
    return 'partial'; // Present in some items
  };

  // Create new tag
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/api/tags/`, {
        name: newTagName.trim(),
        category: 'custom'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newTag = response.data.tag;
      setAllTags(prev => [...prev, newTag]);
      setSelectedTags(prev => [...prev, newTag]);
      setNewTagName('');
      setShowCreateTag(false);
      toast.success('Tag created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    }
  };

  // Apply batch tags
  const handleApply = async () => {
    if (selectedTags.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    setApplying(true);
    try {
      const response = await axios.post(`${API}/api/tags/batch`, {
        content_items: selectedItems.map(item => ({
          content_id: item.id,
          content_type: item.type
        })),
        action: action,
        tag_ids: selectedTags.map(t => t.id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { success_count, failed_count } = response.data;
      
      if (failed_count === 0) {
        toast.success(`Tags ${action === 'add' ? 'added to' : action === 'remove' ? 'removed from' : 'updated on'} ${success_count} items`);
      } else {
        toast.warning(`Updated ${success_count} items, ${failed_count} failed`);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tags');
    } finally {
      setApplying(false);
    }
  };

  // Filter tags by search
  const filteredTags = allTags.filter(tag => 
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              Tag {selectedItems.length} {selectedItems.length === 1 ? 'Item' : 'Items'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Add, remove, or replace tags
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Action Selector */}
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">Action</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAction('add')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      action === 'add' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    Add Tags
                  </button>
                  <button
                    onClick={() => setAction('remove')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      action === 'remove' 
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50'
                    }`}
                  >
                    <Minus className="w-4 h-4" />
                    Remove Tags
                  </button>
                  <button
                    onClick={() => setAction('set')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      action === 'set' 
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                        : 'bg-secondary/30 text-muted-foreground hover:bg-secondary/50'
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Replace All
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {action === 'add' && 'Add selected tags to all items (keeps existing tags)'}
                  {action === 'remove' && 'Remove selected tags from all items'}
                  {action === 'set' && 'Replace all existing tags with selected tags'}
                </p>
              </div>

              {/* Common Tags Indicator */}
              {commonTags.length > 0 && action !== 'set' && (
                <div className="mb-4 p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Tags on all {selectedItems.length} items:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {commonTags.map(tag => {
                      const colors = categoryColors[tag.category] || categoryColors.custom;
                      return (
                        <span
                          key={tag.id}
                          className={`text-xs px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}
                        >
                          {tag.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Search and Create */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowCreateTag(!showCreateTag)}
                    className={showCreateTag ? 'bg-primary/10 border-primary/30' : ''}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Create New Tag */}
                {showCreateTag && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="New tag name..."
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                      className="flex-1"
                      autoFocus
                    />
                    <Button onClick={handleCreateTag} disabled={!newTagName.trim()}>
                      Create
                    </Button>
                  </div>
                )}
              </div>

              {/* Tag List */}
              <div className="space-y-1">
                <label className="text-sm font-medium mb-2 block">
                  Select Tags ({selectedTags.length} selected)
                </label>
                
                {filteredTags.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No tags found</p>
                    <p className="text-xs">Create your first tag above</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {filteredTags.map(tag => {
                      const colors = categoryColors[tag.category] || categoryColors.custom;
                      const selected = isTagSelected(tag.id);
                      const status = getTagStatus(tag.id);
                      
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag)}
                          className={`flex items-center gap-2 p-2 rounded-lg text-left transition-all ${
                            selected 
                              ? `${colors.bg} ${colors.border} border` 
                              : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            selected ? 'bg-white/20' : 'bg-black/20'
                          }`}>
                            {selected && <Check className="w-3 h-3" />}
                          </div>
                          <span className={`flex-1 text-sm truncate ${
                            selected ? colors.text : 'text-foreground'
                          }`}>
                            {tag.name}
                          </span>
                          {status === 'all' && (
                            <CheckCircle2 className="w-3 h-3 text-green-400" title="On all items" />
                          )}
                          {status === 'partial' && (
                            <AlertCircle className="w-3 h-3 text-yellow-400" title="On some items" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Tags Preview */}
              {selectedTags.length > 0 && (
                <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                  <p className="text-xs text-primary mb-2">
                    Will {action === 'add' ? 'add' : action === 'remove' ? 'remove' : 'set'}:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTags.map(tag => {
                      const colors = categoryColors[tag.category] || categoryColors.custom;
                      return (
                        <span
                          key={tag.id}
                          className={`text-xs px-2 py-1 rounded-full ${colors.bg} ${colors.text} ${colors.border} border flex items-center gap-1`}
                        >
                          {tag.name}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTag(tag);
                            }}
                            className="hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={selectedTags.length === 0 || applying}
            className="bg-primary hover:bg-primary/90"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Apply to {selectedItems.length} {selectedItems.length === 1 ? 'Item' : 'Items'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BatchTagModal;
